import { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
  RefreshCw,
  XCircle,
  Zap,
  BarChart3,
  FlaskConical,
} from 'lucide-react';
import { billingApi } from '../../../api/client';
import type { BillingEntitlements, BillingSubscription } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';
import { useSearchParams } from 'react-router-dom';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function planLabel(plan: string): string {
  return { free: 'Free', premium: 'Premium', elite: 'Elite' }[plan] ?? plan;
}

function statusLabel(status: string): string {
  return (
    {
      active: 'Active',
      trialing: 'Trial',
      past_due: 'Past Due',
      paused: 'Paused',
      canceled: 'Canceled',
      expired: 'Expired',
      incomplete: 'Incomplete',
    }[status] ?? status
  );
}

const PLAN_COLORS: Record<string, string> = {
  free:    'text-gray-400 bg-gray-400/10 border-gray-400/20',
  premium: 'text-brand bg-brand/10 border-brand/20',
  elite:   'text-purple-400 bg-purple-400/10 border-purple-400/20',
};

const STATUS_COLORS: Record<string, string> = {
  active:     'text-win bg-win/10',
  trialing:   'text-brand-light bg-brand/10',
  past_due:   'text-loss bg-loss/10',
  paused:     'text-warn bg-warn/10',
  canceled:   'text-gray-400 bg-gray-400/10',
  expired:    'text-gray-400 bg-gray-400/10',
  incomplete: 'text-warn bg-warn/10',
};

function PlanIcon({ plan }: { plan: string }) {
  if (plan === 'elite') return <FlaskConical className="w-5 h-5" />;
  if (plan === 'premium') return <BarChart3 className="w-5 h-5" />;
  return <Zap className="w-5 h-5" />;
}

// ── Main component ─────────────────────────────────────────────────────────────

export function BillingSettingsPage() {
  const { can, hasRole } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const canView   = can('billing.view');
  const canManage = can('billing.manage') && hasRole(['owner']);

  const [entitlements,  setEntitlements]  = useState<BillingEntitlements | null>(null);
  const [subscription,  setSubscription]  = useState<BillingSubscription | null>(null);
  const [isLoading,     setIsLoading]     = useState(true);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [successMsg,    setSuccessMsg]    = useState<string | null>(null);

  // Handle checkout-return params
  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setSuccessMsg('Subscription activated successfully!');
      setSearchParams({}, { replace: true });
    } else if (checkout === 'canceled') {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!canView) return;
    setIsLoading(true);
    Promise.all([billingApi.getEntitlements(), billingApi.getSubscription()])
      .then(([ent, sub]) => {
        setEntitlements(ent);
        setSubscription(sub);
      })
      .catch((err) => setError((err as Error).message ?? 'Failed to load billing data'))
      .finally(() => setIsLoading(false));
  }, [canView]);

  async function handleManage() {
    setLoadingAction('portal');
    setError(null);
    try {
      const { url } = await billingApi.createPortalSession();
      if (url) window.open(url, '_blank');
    } catch (err) {
      setError((err as Error).message ?? 'Failed to open billing portal');
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleCancel() {
    if (!confirm('Cancel your subscription at the end of the current billing period?')) return;
    setLoadingAction('cancel');
    setError(null);
    try {
      await billingApi.cancelSubscription();
      setSuccessMsg('Subscription will cancel at period end.');
      const ent = await billingApi.getEntitlements();
      setEntitlements(ent);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to cancel subscription');
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleReactivate() {
    setLoadingAction('reactivate');
    setError(null);
    try {
      await billingApi.reactivateSubscription();
      setSuccessMsg('Subscription reactivated.');
      const ent = await billingApi.getEntitlements();
      setEntitlements(ent);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to reactivate subscription');
    } finally {
      setLoadingAction(null);
    }
  }

  if (!canView) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view billing.</p>
      </div>
    );
  }

  const { plan, status, isTrialing, trialEndsAt, renewsAt, cancelAtPeriodEnd } = entitlements ?? {};

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Billing</h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage your subscription, payment method, and billing details.
        </p>
      </div>

      {/* Feedback banners */}
      {successMsg && (
        <div className="flex items-center gap-3 p-3 bg-win/10 border border-win/20 rounded-lg">
          <CheckCircle className="w-4 h-4 text-win flex-shrink-0" />
          <p className="text-sm text-win">{successMsg}</p>
          <button
            onClick={() => setSuccessMsg(null)}
            className="ml-auto text-win/60 hover:text-win"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-loss/60 hover:text-loss"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading billing information…
        </div>
      ) : (
        <>
          {/* Plan & status card */}
          {entitlements && plan && (
            <div className="op-panel p-4 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={clsx('p-2 rounded-lg border', PLAN_COLORS[plan] ?? PLAN_COLORS.free)}>
                    <PlanIcon plan={plan} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-100">{planLabel(plan)} Plan</h3>
                      {status && (
                        <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium', STATUS_COLORS[status])}>
                          {statusLabel(status)}
                        </span>
                      )}
                    </div>
                    {isTrialing && trialEndsAt && (
                      <p className="text-xs text-brand-light mt-0.5">
                        Trial ends {formatDate(trialEndsAt)}
                      </p>
                    )}
                    {!isTrialing && renewsAt && status === 'active' && !cancelAtPeriodEnd && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Renews {formatDate(renewsAt)}
                        {subscription?.billingInterval ? ` · billed ${subscription.billingInterval}ly` : ''}
                      </p>
                    )}
                    {cancelAtPeriodEnd && renewsAt && (
                      <p className="text-xs text-warn mt-0.5">
                        Cancels {formatDate(renewsAt)}
                      </p>
                    )}
                  </div>
                </div>

                {canManage && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {plan !== 'free' && cancelAtPeriodEnd && (
                      <button
                        onClick={handleReactivate}
                        disabled={loadingAction === 'reactivate'}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-win/10 text-win border border-win/20 rounded-md hover:bg-win/20 transition-colors disabled:opacity-50"
                      >
                        {loadingAction === 'reactivate' ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <CheckCircle className="w-3 h-3" />
                        )}
                        Reactivate
                      </button>
                    )}
                    {plan !== 'free' && !cancelAtPeriodEnd && status === 'active' && (
                      <button
                        onClick={handleCancel}
                        disabled={loadingAction === 'cancel'}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-400 border border-surface-4 rounded-md hover:border-loss/40 hover:text-loss transition-colors disabled:opacity-50"
                      >
                        {loadingAction === 'cancel' ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={handleManage}
                      disabled={loadingAction === 'portal'}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-surface-3 text-gray-200 border border-surface-4 rounded-md hover:bg-surface-4 transition-colors disabled:opacity-50"
                    >
                      {loadingAction === 'portal' ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <ExternalLink className="w-3 h-3" />
                      )}
                      Billing Portal
                    </button>
                  </div>
                )}
              </div>

              {/* Trial notice */}
              {isTrialing && (
                <div className="bg-brand/10 border border-brand/20 rounded-lg p-4 flex items-start gap-3">
                  <Clock className="w-4 h-4 text-brand-light mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-brand-light">You're on a free trial</p>
                    <p className="text-xs text-brand-light/80 mt-0.5">
                      Your trial ends on {formatDate(trialEndsAt)}. Subscribe before then to keep access.
                    </p>
                  </div>
                </div>
              )}

              {/* Past due notice */}
              {status === 'past_due' && (
                <div className="bg-loss/10 border border-loss/20 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-loss mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-loss">Payment past due</p>
                    <p className="text-xs text-loss/80 mt-0.5">
                      Update your payment method to avoid service interruption.
                    </p>
                    {canManage && (
                      <button
                        onClick={handleManage}
                        className="mt-2 text-xs text-loss hover:text-loss/80 underline"
                      >
                        Update payment method
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subscription details */}
          {subscription && (
            <div className="op-panel p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-300">Subscription Details</h3>
              <dl className="space-y-2">
                {[
                  ['Billing interval', subscription.billingInterval ? `${subscription.billingInterval}ly` : '—'],
                  ['Period start',     formatDate(subscription.currentPeriodStart)],
                  ['Period end',       formatDate(subscription.currentPeriodEnd)],
                  ['Status',          subscription.status ?? '—'],
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-1.5 border-b border-surface-4 last:border-0">
                    <dt className="text-sm text-gray-400">{label}</dt>
                    <dd className="text-sm text-gray-200">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Entitlements */}
          {entitlements && (
            <div className="op-panel p-4 space-y-3">
              <h3 className="text-sm font-medium text-gray-300">Entitlements</h3>
              <dl className="space-y-2">
                {Object.entries(entitlements.capabilities ?? {}).map(([key, enabled]) => (
                  <div key={key} className="flex items-center justify-between py-1.5 border-b border-surface-4 last:border-0">
                    <dt className="text-sm text-gray-400 capitalize">{key.replace(/_/g, ' ')}</dt>
                    <dd>
                      {enabled ? (
                        <CheckCircle className="w-4 h-4 text-win" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-600" />
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {!canManage && (
            <p className="text-xs text-gray-600 text-center">
              Only the organization owner can manage billing and subscriptions.
            </p>
          )}
        </>
      )}
    </div>
  );
}
